/**
 * webglManager.js — production ready
 * ─────────────────────────────────────────────────────────────────
 * Solves two problems:
 *   1. Context LIMIT  — browsers allow ~8-16 WebGL contexts total.
 *      Multiple Three.js canvases + React StrictMode + navigation
 *      exhausts this quickly. acquireWebGL() ensures only ONE context
 *      ever exists across the whole app.
 *
 *   2. Context LOSS   — Chrome suspends GPU memory for background
 *      tabs to save resources. When a user switches away and returns,
 *      the WebGL context is gone. onContextLost/Restored handles
 *      automatic recovery without a page reload.
 *
 * USAGE in any Three.js component:
 *
 *   import { acquireWebGL, releaseWebGL, registerRenderer } from './webglManager'
 *
 *   useEffect(() => {
 *     acquireWebGL()
 *     setReady(true)
 *     return () => releaseWebGL()
 *   }, [])
 *
 *   <Canvas
 *     onCreated={({ gl }) => registerRenderer(gl)}
 *   />
 */

let _activeRenderer = null;
let _onLostCallback = null; // called when context is lost
let _onRestoredCallback = null; // called when context is restored

/**
 * Call before mounting a Canvas.
 * Disposes any existing renderer so the browser slot is free.
 */
export function acquireWebGL() {
  if (_activeRenderer) {
    try {
      _activeRenderer.dispose();
      _activeRenderer.forceContextLoss();
    } catch {
      /* already lost — safe to ignore */
    }
    _activeRenderer = null;
  }
}

/**
 * Register the renderer after Canvas creation.
 * Also attaches context loss/restore handlers for tab-switch recovery.
 *
 * @param {WebGLRenderer} gl        - from onCreated({ gl })
 * @param {Function} onLost         - called when context is lost (hide canvas)
 * @param {Function} onRestored     - called when context is restored (show canvas)
 */
export function registerRenderer(gl, onLost, onRestored) {
  _activeRenderer = gl;
  _onLostCallback = onLost;
  _onRestoredCallback = onRestored;

  const canvas = gl.domElement;

  const handleLost = (e) => {
    e.preventDefault(); // required — without this, context cannot be restored
    console.warn("[webglManager] WebGL context lost");
    if (_onLostCallback) _onLostCallback();
  };

  const handleRestored = () => {
    console.log("[webglManager] WebGL context restored");
    if (_onRestoredCallback) _onRestoredCallback();
  };

  canvas.addEventListener("webglcontextlost", handleLost, false);
  canvas.addEventListener("webglcontextrestored", handleRestored, false);

  // Store cleanup so releaseWebGL can remove listeners
  gl._fhCleanupLost = () => {
    canvas.removeEventListener("webglcontextlost", handleLost);
    canvas.removeEventListener("webglcontextrestored", handleRestored);
  };
}

/**
 * Call in the cleanup of the useEffect that called acquireWebGL.
 */
export function releaseWebGL() {
  if (_activeRenderer) {
    try {
      if (_activeRenderer._fhCleanupLost) _activeRenderer._fhCleanupLost();
      _activeRenderer.dispose();
      _activeRenderer.forceContextLoss();
    } catch {
      /* already lost */
    }
    _activeRenderer = null;
  }
  _onLostCallback = null;
  _onRestoredCallback = null;
}
